import React, { useCallback } from 'react';
import {
  Grid, Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import ContactForm from '../ContactForm/ContactForm';
import ContactFormResult from '../ContactFormResult/ContactFormResult';
import Button from '@/shared/molecules/Button';
import constants from '@/shared/constants/contact-us-page';

import commonCss from '../common/CommonStyles.module.css';
import css from './ContactPage.module.css';
import globalCss from '../../../styles/globals.module.css';
import ChatLiveAgent from '../ChatLiveAgent';
import ServiceError from '@/shared/molecules/ServiceError';

const useStyles = makeStyles(() => ({
  legacyButton: {
    fontFamily: 'open_sans_semi',
    textTransform: 'none',
    color: 'var(--primary-info-700)',
    padding: '2px',
    transform: 'translateX(-2px)'
  },
  container: {
    width: '100%'
  }
}));

const ContactPage = (): JSX.Element | null => {
  const router = useRouter();
  const classes = useStyles();
  const [showingResults, setShowingResults] = React.useState(false);
  const [serviceError, setServiceError] = React.useState<Error | undefined>();

  const onFormSubmitted = useCallback((withError?: Error) => {
    setServiceError(withError);

    if (!withError) {
      setShowingResults(true);
    }

    // scroll body to top to make sure the user sees the results
    // ensure scrolling happens after scheduled DOM updates
    setTimeout(() => {
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }, 0);
  }, [setShowingResults]);

  const openLink = () => {
    router.push(constants.FAQ_LINK);
  }

  const ContactUsBtnWrapper = (
    <Grid container className={clsx(css?.contactUsBtnGridContainer, globalCss?.regularMarginBottom)}>
      <ChatLiveAgent />
      <Grid
        item
        className={commonCss?.contactUsBtnGridItem}
      >
        <Button
          id="faqs-button-contact-page"
          type="button"
          buttonBaseTag="a"
          label="FAQs"
          size="large"
          fullWidth
          color="secondary"
          variantStyle="outlined"
          onClick={openLink}
        />
      </Grid>
    </Grid>
  );

  return (
    <Grid container>
      <Grid item className={classes.container}>
        <Typography variant="h1" className={globalCss?.pageHeader} data-testid="contact-page-header">
          Contact us
        </Typography>
        <Typography variant="h3" className={globalCss?.regularMarginBottom} hidden={showingResults}>
          Looking for a quick answer?
        </Typography>
        {!showingResults ? ContactUsBtnWrapper : null}
        <Typography hidden={showingResults}>
          For help with an order in progress, please call the restaurant listed in your confirmation email.
        </Typography>
        <hr className={css.hr} hidden={showingResults} />
        <Typography variant="h3" className={globalCss?.regularMarginBottom} hidden={showingResults}>
          Still have questions or feedback?
        </Typography>
        {showingResults ? ContactUsBtnWrapper : null}
          <>
            {showingResults && !serviceError ? <ContactFormResult /> : null}
            {serviceError ? <ServiceError /> : null}
            {!showingResults || serviceError ? <ContactForm onFormSubmitted={onFormSubmitted} /> : null}
          </>
      </Grid>
    </Grid>
  );
};

export default ContactPage;
